// Whipper snipper: cuts a 3×3-cell patch one cell in front of the walker
// (including the trim ring the mower can't reach) to the range midpoint, and
// clears weeds.

import type { Ctx } from '../ctx';
import { T_GRASS, T_PAVED } from '../layout';
import { setLength } from '../cells';
import { trimTargetMm } from '../vibe';

export function stepTrimmer(ctx: Ctx): void {
  const g = ctx.grid;
  const op = ctx.op;
  const cm = g.cellM;
  const reach = Math.max(0.5, cm);
  const hx = Math.floor((op.x + Math.cos(op.heading) * reach) / cm);
  const hy = Math.floor((op.y + Math.sin(op.heading) * reach) / cm);
  for (let cy = hy - 1; cy <= hy + 1; cy++) {
    if (cy < 0 || cy >= g.h) continue;
    for (let cx = hx - 1; cx <= hx + 1; cx++) {
      if (cx < 0 || cx >= g.w) continue;
      const i = cy * g.w + cx;
      let changed = false;
      if (g.weed[i]) {
        g.weed[i] = 0;
        ctx.weedsCleared++;
        changed = true;
      }
      if (g.type[i] === T_GRASS && !g.dnm[i]) {
        // Feature P9: with over-cut on, the line scalps open lawn to ~15 mm (the trim ring keeps the midpoint).
        const target = trimTargetMm((g.lo[i] + g.hi[i]) / 2, !!g.mowable[i], ctx.trimOvercut);
        if (g.lengthMm[i] > target) {
          setLength(ctx, i, target);
          changed = true;
          if (ctx.blowBack) throwClippings(ctx, cx, cy);
        }
        if (!g.cut[i]) {
          if (g.trim[i]) ctx.ringTrimmed++;
          g.cut[i] = 1;
          changed = true;
        }
      }
      if (changed) g.dirty.push(i);
    }
  }
}

/**
 * Feature P9 (SimOptions.blowBack): a snip beside the paving throws clippings
 * onto one clean paved neighbour (grid.clip). They count against a clear until
 * the blower moves them (tools/blower.ts).
 */
function throwClippings(ctx: Ctx, cx: number, cy: number): void {
  const g = ctx.grid;
  const clip = g.clip;
  if (!clip) return;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const x = cx + dx, y = cy + dy;
      if ((!dx && !dy) || x < 0 || y < 0 || x >= g.w || y >= g.h) continue;
      const j = y * g.w + x;
      if (g.type[j] !== T_PAVED || clip[j]) continue;
      clip[j] = 1;
      ctx.clipList.push(j);
      g.dirty.push(j);
      return;
    }
  }
}
